import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { SkipLink } from "./SkipLink";
import { OfflineBanner } from "@/components/states";
import { OnboardingNudge } from "@/components/onboarding/OnboardingNudge";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useUIStore } from "@/stores/uiStore";
import { FloatingFeedbackButton } from "@/features/feedback/components/FloatingFeedbackButton";
import { DisplayNameModal } from "@/components/auth/DisplayNameModal";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isAdmin } = useCurrentUser();
  const location = useLocation();
  const drawerOpen = useUIStore((s) => s.drawerOpen);
  const setDrawerOpen = useUIStore((s) => s.setDrawerOpen);

  // Close the mobile drawer on any route change (covers programmatic
  // navigate() and back/forward). Outside-click + Esc are handled by Sheet.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, setDrawerOpen]);

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-background bg-glow">
      <SkipLink />
      <OfflineBanner />
      <OnboardingNudge />
      <div className="flex flex-1 min-h-0 w-full">
        {/* Persistent sidebar — desktop only (>= 1024px). */}
        <div className="hidden lg:block h-full">
          <AppSidebar isAdmin={isAdmin} variant="persistent" />
        </div>

        {/* Mobile drawer — < 1024px. */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent
            id="app-sidebar-drawer"
            side="left"
            className="lg:hidden p-0 w-64 sm:max-w-xs"
          >
            <VisuallyHidden>
              <SheetTitle>Menu de navegação</SheetTitle>
            </VisuallyHidden>
            <AppSidebar isAdmin={isAdmin} variant="drawer" />
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col overflow-hidden">
          <AppHeader />
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-y-auto p-4 sm:p-6 focus:outline-none"
          >
            {children}
          </main>
        </div>
      </div>
      <FloatingFeedbackButton />
      <DisplayNameModal />
    </div>
  );
}
